'use client';

import { Check, Circle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PASSWORD_MAX_BYTES, PASSWORD_RULES, passwordBytes } from '@/lib/auth/password-rules';

/**
 * Inline strength checklist shown under a new-password input. Each rule ticks
 * as soon as the typed value meets it, so the user sees what is missing before
 * they submit. The over-length line appears only once it applies.
 */
export function PasswordRules({ value, id }: { value: string; id?: string }) {
  const tooLong = passwordBytes(value) > PASSWORD_MAX_BYTES;

  return (
    <ul id={id} aria-live="polite" className="space-y-1 text-xs" data-testid="password-rules">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(value);

        return (
          <li
            key={rule.id}
            data-rule={rule.id}
            data-met={met}
            className={cn('flex items-center gap-1.5', met ? 'text-cs-success-400' : 'text-cs-text-muted')}
          >
            {met ? <Check className="h-3 w-3" aria-hidden /> : <Circle className="h-3 w-3" aria-hidden />}
            <span>
              {rule.label}
              <span className="sr-only">{met ? ' (met)' : ' (not met)'}</span>
            </span>
          </li>
        );
      })}
      {tooLong && (
        <li className="flex items-center gap-1.5 text-cs-error-400">
          Too long — use at most {PASSWORD_MAX_BYTES} bytes
        </li>
      )}
    </ul>
  );
}
